import { lazy, Suspense, useEffect, useState } from 'react';
import { authApi } from './lib/api-client';
import { BrowserRouter, Routes, Route, Navigate } from 'react-router-dom';
import { useAuthStore } from './stores/auth-store';
import { LoginPage } from './pages/login';
import { DashboardPage } from './pages/dashboard';
import RegistrationsPage from './pages/registrations';
import MembersPage from './pages/members';
import FormsPage from './pages/forms';
import ContentPage from './pages/content';
import MeetingsPage from './pages/meetings';
import OnboardingReviewPage from './pages/onboarding-review';
import BankingReviewPage from './pages/banking-review';
import FundingApplicationsPage from './pages/funding-applications';
import ProgramSettingsPage from './pages/program-settings';
import CorrectionsPage from './pages/corrections';
import { Layout } from './components/layout';

const MeetingRoomPage = lazy(() => import('./pages/meeting-room'));

// OCN Admin Portal - v1.0.0
export function App() {
  return (
    <BrowserRouter>
      <Routes>
        <Route path="/login" element={<LoginPage />} />
        <Route
          path="/*"
          element={
            <ProtectedRoute>
              <Layout>
                <Routes>
                  <Route path="/" element={<Navigate to="/dashboard" replace />} />
                  <Route path="/dashboard" element={<DashboardPage />} />
                  <Route path="/registrations" element={<RegistrationsPage />} />
                  <Route path="/members" element={<MembersPage />} />
                  <Route path="/onboarding-review" element={<OnboardingReviewPage />} />
                  <Route path="/banking-review" element={<BankingReviewPage />} />
                  <Route path="/funding-applications" element={<FundingApplicationsPage />} />
                  <Route path="/program-settings" element={<ProgramSettingsPage />} />
                  <Route path="/corrections" element={<CorrectionsPage />} />
                  <Route path="/forms" element={<FormsPage />} />
                  <Route path="/content" element={<ContentPage />} />
                  <Route path="/meetings" element={<MeetingsPage />} />
                  <Route
                    path="/meetings/:id/room"
                    element={
                      <Suspense fallback={<p role="status">Loading meeting...</p>}>
                        <MeetingRoomPage />
                      </Suspense>
                    }
                  />
                </Routes>
              </Layout>
            </ProtectedRoute>
          }
        />
      </Routes>
    </BrowserRouter>
  );
}

function ProtectedRoute({ children }: { children: React.ReactNode }) {
  const { isAuthenticated, accessToken, user, setUser } = useAuthStore();
  const [verified, setVerified] = useState(false);
  const [error, setError] = useState('');
  const [attempt, setAttempt] = useState(0);

  useEffect(() => {
    let active = true;
    if (!isAuthenticated || !accessToken) return;
    authApi
      .getProfile()
      .then((profile) => {
        if (!active) return;
        setUser(profile);
        setVerified(true);
      })
      .catch((err) => {
        if (!active) return;
        if (err.response?.status === 401) useAuthStore.getState().logout();
        else setError('Unable to verify your session. Please try again.');
      });
    return () => {
      active = false;
    };
  }, [isAuthenticated, setUser, attempt]);

  if (!isAuthenticated || !accessToken) return <Navigate to="/login" replace />;
  if (error)
    return (
      <div role="alert">
        {error}{' '}
        <button
          onClick={() => {
            setError('');
            setAttempt((n) => n + 1);
          }}
        >
          Retry
        </button>
      </div>
    );
  if (!verified) return <p role="status">Checking session...</p>;
  if (!user?.roles.includes('admin')) return <div role="alert">Administrator access required.</div>;
  return <>{children}</>;
}
